import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { directingDigest, directingEntries, directingPlan, type DirectedEntry, type DirectingScope } from '../../supabase/functions/_shared/scene-directing';
import type { ScreenplayEditorBlock } from './screenplayEditor';
import { GEMINI_DIRECTION_GROUPS } from '../../supabase/functions/_shared/gemini-directions';

export async function analyzeSceneInflections(input: { screenplayId: string; blocks: ScreenplayEditorBlock[]; entryId: string; scope: DirectingScope;
  throughEntryId?: string; conversionRunId?:string; signal: AbortSignal; onProgress: (done: number, total: number,lastEntryId?:string) => void;onWaiting?:(message:string)=>void }) {
  const entries = directingEntries(input.blocks);
  const plan = directingPlan(entries, input.scope, input.entryId, input.throughEntryId);
  const signature = await directingDigest(entries);
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) throw new Error('Please sign in again.');
  const results: DirectedEntry[] = [];
  let charge = 0;
  let lastEntryId:string|undefined;
  const waitUntil=async(at:number,message:string)=>{
    input.onWaiting?.(message);
    while(Date.now()<at){
      input.signal.throwIfAborted();
      await new Promise<void>((resolve,reject)=>{
        const stop=()=>{clearTimeout(timer);reject(input.signal.reason??new DOMException('Paused','AbortError'));};
        const timer=setTimeout(()=>{input.signal.removeEventListener('abort',stop);resolve();},Math.min(30000,at-Date.now()));
        input.signal.addEventListener('abort',stop,{once:true});
      });
    }
  };
  for (let batch = 0; batch < plan.length; batch++) {
    input.signal.throwIfAborted();
    input.onProgress(batch, plan.length,lastEntryId);
    let body:Record<string,any>,attempt=0,checks=0;
    while(true){
    input.signal.throwIfAborted();
    if(++checks>250)throw new Error('Analysis is still waiting. Pause and continue to reuse saved batches.');
    const session=await supabase.auth.getSession();
    if(session.error||!session.data.session)throw new Error('Please sign in again to continue the saved analysis.');
    let response:Response;
    try{response = await fetch(`${SUPABASE_URL}/functions/v1/scene-directing`, { method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: SUPABASE_PUBLISHABLE_KEY, Authorization: `Bearer ${session.data.session.access_token}` },
      signal: AbortSignal.any([input.signal,AbortSignal.timeout(125000)]), body: JSON.stringify({ screenplay_id: input.screenplayId, scope: input.scope, entry_id: input.entryId, batch, signature, consent: true,
        ...(input.throughEntryId === undefined ? {} : { through_entry_id: input.throughEntryId }),
        ...(input.conversionRunId?{conversion_run_id:input.conversionRunId,recovery_attempt:attempt}:{}) }) });
    }catch(error){
      input.signal.throwIfAborted();
      if(!input.conversionRunId)throw error;
      await waitUntil(Date.now()+15000,'Connection interrupted. Checking the saved batch receipt before doing more work…');continue;
    }
    try { body = await response.json(); } catch {
      if(!input.conversionRunId)throw new Error('The analysis response was interrupted. No automatic retry was made.');
      await waitUntil(Date.now()+15000,'Checking the saved analysis receipt…');continue;
    }
    if(response.ok)break;
    if(input.conversionRunId){
      if(body.code==='analysis_uncertain'&&attempt===0){attempt=1;input.onWaiting?.('Recovering this batch with the one replacement included in your confirmation…');continue;}
      if(['analysis_pending','analysis_rate_limit','analysis_daily_limit'].includes(body.code)){
        const at=Date.parse(body.retry_at);
        if(Number.isFinite(at)&&at>Date.now()&&at-Date.now()<=26*60*60*1000){await waitUntil(at,body.code==='analysis_pending'?'This batch is still processing. Waiting for its saved result…':`Analysis is queued until ${new Date(at).toLocaleString()}. Completed pages are preserved.`);continue;}
      }
    }
    throw new Error(typeof body.error === 'string' ? body.error : 'Scene analysis is unavailable.');
    }
    const expected = plan[batch].targets.map(b => b.id);
    if (body.batch !== batch || body.batches !== plan.length || !Array.isArray(body.entries) || body.entries.length !== expected.length ||
      !Number.isSafeInteger(body.charge_micro_usd) || body.charge_micro_usd < 0 || body.charge_micro_usd > 100000 || typeof body.cached !== 'boolean') throw new Error('Invalid scene analysis response.');
    const seen = new Set<string>();
    for (const row of body.entries) {
      if (!row || !expected.includes(row.id) || seen.has(row.id) || !row.direction || typeof row.direction !== 'object' || Array.isArray(row.direction)) throw new Error('Invalid directing entry.');
      for (const [key, value] of Object.entries(row.direction)) {
        const group = GEMINI_DIRECTION_GROUPS[key as keyof typeof GEMINI_DIRECTION_GROUPS];
        if (!group || typeof value !== 'string' || !(group.options as readonly string[]).includes(value)) throw new Error('Unsupported inflection returned.');
      }
      if (!Object.keys(row.direction).length) throw new Error('Missing performance direction.');
      seen.add(row.id); results.push(row);
    }
    if (!body.cached) charge += body.charge_micro_usd;
    lastEntryId=plan[batch].targets.at(-1)?.id;
    input.onProgress(batch+1,plan.length,lastEntryId);
  }
  input.signal.throwIfAborted();
  input.onProgress(plan.length, plan.length,lastEntryId);
  return { entries: results, chargeMicroUsd: charge };
}
